"use client"

import { useState, useEffect } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useNavigate, Link } from "react-router-dom"
import { logout } from "../slices/authSlice"
import { toast } from "react-toastify"
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Menu,
  MenuItem,
  useTheme,
  useMediaQuery,
  Container,
} from "@mui/material"
import {
  Menu as MenuIcon,
  ChevronLeft as ChevronLeftIcon,
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  LocationCity as LocationCityIcon,
  Person as PersonIcon,
  Logout as LogoutIcon,
  Notifications as NotificationsIcon,
  Message as NonWhatsappIcon,
  List as WhiteListIcon,
  Description as TermsIcon,
  Tune as CampaignConfigIcon,
  AdminPanelSettings as AdminPanelSettingsIcon,
  Send as SendIcon,
  SmartButton as ButtonIcon,
  Schedule as ScheduleIcon,
  History as HistoryIcon,
} from "@mui/icons-material"
import RunningMessage from "./RunningMessage"

const drawerWidth = 240

const DashboardLayout = ({ children }) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down("md"))
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { userInfo = {} } = useSelector((state) => state.auth || {})
  const [open, setOpen] = useState(!isMobile)
  const [anchorEl, setAnchorEl] = useState(null)
  const menuOpen = Boolean(anchorEl)

  useEffect(() => {
    setOpen(!isMobile)
  }, [isMobile])

  const handleDrawerToggle = () => {
    setOpen(!open)
  }

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget)
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
  }

  const handleLogout = async () => {
    try {
      dispatch(logout())
      toast.success("Logged out successfully")
      navigate("/login")
    } catch (error) {
      console.error("Logout error:", error)
      toast.error("Failed to log out")
    }
    handleMenuClose()
  }

  const handleProfileClick = () => {
    navigate("/profile")
    handleMenuClose()
  }

  // Menu items based on user role
  const menuItems = [
    { text: "Dashboard", icon: <DashboardIcon />, href: "/dashboard" },
    { text: "Create Campaign", icon: <SendIcon />, href: "/send-campaign" },
    { text: "Create Button Campaign", icon: <ButtonIcon />, href: "/button-campaign" },
    { text: "My Campaigns", icon: <HistoryIcon />, href: "/my-campaigns" },
  ]

  // Show admin-only menu items
  if (userInfo?.user?.role === "admin") {
    menuItems.push(
      { text: "Pending Campaign", icon: <ScheduleIcon />, href: "/pending-campaign" },
      { text: "User Management", icon: <PeopleIcon />, href: "/users" },
      { text: "Cities", icon: <LocationCityIcon />, href: "/locations" },
      { text: "Admin Staff", icon: <AdminPanelSettingsIcon />, href: "/admin-staff" },
      { text: "Non-WhatsApp No.", icon: <NonWhatsappIcon />, href: "/non-whatsapp" },
      { text: "White Listing No.", icon: <WhiteListIcon />, href: "/white-listing" },
      { text: "Campaign Config", icon: <CampaignConfigIcon />, href: "/campaign-config" },
      // { text: "Transaction Management", icon: <HistoryIcon />, href: "/transactions" },
    )
  }
  // Show user management for resellers too
  else if (userInfo?.user?.role === "reseller") {
    menuItems.push({ text: "User Management", icon: <PeopleIcon />, href: "/users" })
    // menuItems.push({ text: "Transaction Management", icon: <HistoryIcon />, href: "/transactions" })
  }

  // Add settings and terms to all users
  menuItems.push(
    { text: "Terms & Conditions", icon: <TermsIcon />, href: "/terms" },
    { text: "Transaction Management", icon: <HistoryIcon />, href: "/transactions" },
  )

  return (
    <Box sx={{ display: "flex", height: "100vh" }}>
      <AppBar
        position="fixed"
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
          transition: theme.transitions.create(["width", "margin"], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
          ...(open && {
            marginLeft: drawerWidth,
            width: `calc(100% - ${drawerWidth}px)`,
            transition: theme.transitions.create(["width", "margin"], {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
          }),
        }}
      >
        <Toolbar>
          <IconButton color="inherit" aria-label="open drawer" onClick={handleDrawerToggle} edge="start" sx={{ mr: 2 }}>
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            WhatsApp Campaign
          </Typography>
          <IconButton color="inherit" sx={{ mr: 1 }}>
            <NotificationsIcon />
          </IconButton>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Typography variant="body2" sx={{ mr: 1 }}>
              {userInfo?.user?.name || "User"}
            </Typography>
            <IconButton
              onClick={handleProfileMenuOpen}
              size="small"
              sx={{ ml: 1 }}
              aria-controls={menuOpen ? "account-menu" : undefined}
              aria-haspopup="true"
              aria-expanded={menuOpen ? "true" : undefined}
            >
              <Avatar
                alt={userInfo?.user?.name || "User"}
                src={
                  userInfo?.user?.profileImage
                    ? userInfo.user.profileImage.startsWith("/")
                      ? `${process.env.REACT_APP_API_URL || "http://localhost:5000"}${userInfo.user.profileImage}`
                      : userInfo.user.profileImage
                    : ""
                }
                sx={{ width: 32, height: 32, bgcolor: "primary.dark" }}
              >
                {userInfo?.user?.name?.charAt(0) || "U"}
              </Avatar>
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>
      <Menu
        anchorEl={anchorEl}
        id="account-menu"
        open={menuOpen}
        onClose={handleMenuClose}
        onClick={handleMenuClose}
        PaperProps={{
          elevation: 0,
          sx: {
            overflow: "visible",
            filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.32))",
            mt: 1.5,
            "& .MuiAvatar-root": {
              width: 32,
              height: 32,
              ml: -0.5,
              mr: 1,
            },
            "&:before": {
              content: '""',
              display: "block",
              position: "absolute",
              top: 0,
              right: 14,
              width: 10,
              height: 10,
              bgcolor: "background.paper",
              transform: "translateY(-50%) rotate(45deg)",
              zIndex: 0,
            },
          },
        }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        <MenuItem onClick={handleProfileClick}>
          <ListItemIcon>
            <PersonIcon fontSize="small" />
          </ListItemIcon>
          Profile
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          Logout
        </MenuItem>
      </Menu>
      <Drawer
        variant={isMobile ? "temporary" : "permanent"}
        open={open}
        onClose={isMobile ? handleDrawerToggle : undefined}
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          [`& .MuiDrawer-paper`]: {
            width: drawerWidth,
            boxSizing: "border-box",
            ...(isMobile
              ? {}
              : {
                  position: "relative",
                }),
          },
        }}
      >
        <Toolbar
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            px: [1],
          }}
        >
          <Typography variant="h6" component="div" sx={{ flexGrow: 1, color: "white", fontWeight: "bold" }}>
            WhatsApp Campaign
          </Typography>
          <IconButton onClick={handleDrawerToggle} sx={{ color: "white" }}>
            <ChevronLeftIcon />
          </IconButton>
        </Toolbar>
        <Divider sx={{ borderColor: "rgba(255, 255, 255, 0.12)" }} />
        <List component="nav">
          {menuItems.map((item) => (
            <ListItem key={item.text} disablePadding>
              <ListItemButton
                component={Link}
                to={item.href}
                sx={{
                  minHeight: 48,
                  px: 2.5,
                  "&.Mui-selected": {
                    backgroundColor: "rgba(255, 255, 255, 0.2)",
                  },
                  "&:hover": {
                    backgroundColor: "rgba(255, 255, 255, 0.1)",
                  },
                }}
                selected={window.location.pathname.startsWith(item.href)}
              >
                <ListItemIcon sx={{ color: "white", minWidth: 40 }}>{item.icon}</ListItemIcon>
                <ListItemText
                  primary={item.text}
                  sx={{
                    opacity: open ? 1 : 0,
                    "& .MuiTypography-root": { color: "white" },
                  }}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Drawer>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { sm: `calc(100% - ${open ? drawerWidth : 0}px)` },
          overflow: "auto",
          height: "100vh",
          pt: 10,
        }}
      >
        <Container maxWidth="xl">
          <Box sx={{ position: "sticky", zIndex: 1000, mb: 3 }}>
            <RunningMessage />
          </Box>
          {children}
        </Container>
      </Box>
    </Box>
  )
}

export default DashboardLayout
